import { AnalyticsContext } from 'src/utils/analytics/events';
import {
  CarouselItemListName,
  GTMCartItem,
  ProductRecommendationSection,
  ProductRecommendationSource,
  PRODUCT_RECOMMENDATION,
} from '../internal-gtm-tracker.types';
import { PageTypes } from './get-page-type';

export function generateItemListNameForCarouselItems({
  source = ProductRecommendationSource.native,
  section = ProductRecommendationSection.custom,
}): CarouselItemListName {
  const recommendationSource: string = ProductRecommendationSource[source] ?? source;
  const recommendationSection: string = ProductRecommendationSection[source] ?? section;
  return `${PRODUCT_RECOMMENDATION} > ${recommendationSource as ProductRecommendationSource} > ${
    recommendationSection as ProductRecommendationSection
  }` as CarouselItemListName;
}

type BuildGTMNavigationBreadcrumbsArgs = {
  category?: { value: string; key: string } | null;
  currentSpecial?: { name: string; specialType: string } | null;
  heading: string;
  pageType: PageTypes | null;
};

export function buildGTMNavigationBreadcrumbs({
  category,
  currentSpecial,
  heading,
  pageType,
}: BuildGTMNavigationBreadcrumbsArgs): string | null {
  if (pageType === PageTypes.categoryPage) {
    if (category?.value) {
      return `Category > ${category.value} > ${heading}`;
    }
  }

  if (pageType === PageTypes.searchResultsPage) {
    return `Search Results`;
  }

  if (pageType === PageTypes.brandsPage) {
    return heading === 'Brands' ? heading : `Brands > ${heading}`;
  }

  if (pageType === PageTypes.specialsPage) {
    if (currentSpecial) {
      return `Specials > ${currentSpecial.specialType === 'bogo' ? `Offer` : `Sale`} > ${currentSpecial.name}`;
    }
    return `Specials`;
  }

  return null;
}

type InjectItemListNameAndIndexArgs = {
  context: AnalyticsContext;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  GTMItem: GTMCartItem & { affiliation?: string | null };
  page: PageTypes | null;
};
// eslint-disable-next-line @typescript-eslint/naming-convention
export function injectItemListNameAndIndex({ context, GTMItem, page }: InjectItemListNameAndIndexArgs): void {
  if (page !== PageTypes.productPage || context.activeProductPosition?.fromCarousel) {
    const itemListName = setItemListName({ context, page });

    if (itemListName) {
      GTMItem.item_list_name = itemListName;
      GTMItem.index = context.activeProductPosition?.index;
    }
  }
}

export function setItemListName({ context, page }: Partial<InjectItemListNameAndIndexArgs>): string | null {
  if (context && (page !== PageTypes.productPage || context.activeProductPosition?.fromCarousel)) {
    const itemListName = context.activeProductPosition?.fromCarousel
      ? generateItemListNameForCarouselItems({
          section: context.activeProductPosition.section,
          source: context.activeProductPosition.source,
        })
      : context.activeNavigationBreadcrumbs ?? null;

    return itemListName;
  }
  return null;
}
